/* ========== useAnchoredPopover ==========
 * Ported from DXF Viewer's components/useAnchoredPopover.ts. Viewport-
 * aware positioning for popovers anchored to a trigger.
 *
 * Placement strategy:
 *   - Horizontal: right-align with the trigger's right edge; flip to
 *     left-align if that overflows.
 *   - Vertical: below the trigger first; flip above if no room below;
 *     as a last resort clamp and scroll.
 *
 * Popover is hidden on the first frame so it can be measured, then
 * positioned in a useLayoutEffect so there's no visible flash.
 */

import * as React from "react";
import { useLayoutEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";

type AnchoredPopoverResult = {
  popoverRef: React.RefObject<HTMLDivElement>;
  style: CSSProperties;
};

const HIDDEN_STYLE: CSSProperties = {
  position: "fixed",
  top: 0,
  left: 0,
  visibility: "hidden",
};

export function useAnchoredPopover(
  triggerRef: React.RefObject<HTMLElement | null>,
  open: boolean,
): AnchoredPopoverResult {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<CSSProperties>(HIDDEN_STYLE);

  useLayoutEffect(() => {
    if (!open) {
      setStyle(HIDDEN_STYLE);
      return;
    }
    const trigger = triggerRef.current;
    const popover = popoverRef.current;
    if (!trigger || !popover) return;

    const gap = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const tr = trigger.getBoundingClientRect();
    const pr = popover.getBoundingClientRect();
    const pw = pr.width;
    const ph = pr.height;

    let left: number;
    let top: number;
    let maxHeight: number;

    // Horizontal: right-align with trigger; fall back to left-align.
    const rightAligned = tr.right - pw;
    if (rightAligned >= gap) {
      left = rightAligned;
    } else if (tr.left + pw + gap <= vw) {
      left = tr.left;
    } else {
      left = Math.max(gap, vw - pw - gap);
    }

    // Vertical: below first, above as fallback.
    if (tr.bottom + gap + ph + gap <= vh) {
      top = tr.bottom + gap;
      maxHeight = vh - top - gap;
    } else if (tr.top - gap - ph >= gap) {
      top = tr.top - gap - ph;
      maxHeight = ph;
    } else {
      top = tr.bottom + gap;
      maxHeight = vh - top - gap;
    }

    setStyle({
      position: "fixed",
      top,
      left,
      maxHeight,
      overflowY: "auto",
      visibility: "visible",
    });
  }, [open, triggerRef]);

  return { popoverRef, style };
}
